<template>
  <div
    class="app-dialer-tabs"
    :class="states"
  >
    <!-- Inline tabs are all visible at the same time -->
    <template v-if="type === 'inline'">
      <div
        v-for="tab of tabsWithKeys"
        :key="tab.key"
        class="tab"
        :class="{ 'is-active': tab.key === activeTabId }"
        @click="onTabChange(tab)"
      >
        <span class="label">
          {{ getTabTitle(tab) }}
        </span>
      </div>
    </template>

    <!-- In compact mode other tabs are visible as options in a column -->
    <template v-if="type === 'compact'">
      <div
        class="toggle-button"
        :class="{ 'is-open': isOpen }"
        @click="onOpenClick"
      >
        <or-icon icon="expand_more" />

        <span class="label">
          {{ activeTabLabel }}
        </span>
      </div>

      <template v-if="isOpen">
        <div
          v-for="option of options"
          :key="option.key"
          class="tab-option"
          @click="onTabChange(option)"
        >
          <span class="label">
            {{ getTabTitle(option) }}
          </span>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { TranslateResult } from 'vue-i18n';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import { PopupTab } from '../../types';
import { SIZE_PROP, TABS_DISPLAY_PROP } from '../../constants';

/**
 * When tab doesn't have a key, we are going to
 * use and emit stringified tab index as a key.
 */
export default defineComponent({
  name: 'AppDialerTabs',
  components: {
    OrIcon,
  },
  props: {
    tabs: {
      type: Array as PropType<PopupTab[]>,
      default: () => [],
    },
    activeTabId: {
      type: String,
      default: '',
    },
    size: SIZE_PROP,
    type: TABS_DISPLAY_PROP,
    isDisabled: Boolean,
  },
  emits: ['tab-change'],
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    /** Add an index as a key if it isn't present */
    tabsWithKeys(): PopupTab[] {
      return this.tabs.map((tab, index) => {
        return { key: index.toString(), ...tab };
      });
    },

    states(): object {
      return {
        [`is-${this.type}`]: true,
        [`is-${this.size}`]: true,
      };
    },

    activeTabLabel(): TranslateResult {
      const tab = this.tabsWithKeys.find(tab => tab.key === this.activeTabId);
      return tab ? this.getTabTitle(tab) : '';
    },

    options(): PopupTab[] {
      return this.tabsWithKeys.filter(tab => tab.key !== this.activeTabId);
    },
  },
  methods: {
    getTabTitle(tab: PopupTab): TranslateResult {
      if (tab.label) {
        return tab.label;
      }

      const index = this.tabsWithKeys.findIndex(i => i.key === tab.key);
      const data = { number: index + 1 };
      return this.$t('hitl.modules.dialer.default-tab-title', data);
    },

    onOpenClick() {
      if (!this.isDisabled) {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
          setTimeout(() => {
            document.addEventListener('click', this.onClose);
          });
        }
      }
    },

    onClose() {
      this.isOpen = false;
      document.removeEventListener('click', this.onClose);
    },

    onTabChange(tab: PopupTab) {
      if (!this.isDisabled) {
        this.$emit('tab-change', tab.key);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

.app-dialer-tabs {
  --tabs-padding: 0.125rem;
  --tabs-border-radius: 0.25rem;
  --tab-height: 1.875rem;
  --tab-font-size: var(--fs-0);
  --tab-icon-font-size: 1.25rem;
  --tab-padding: 0.5rem;

  display: flex;
  border-radius: var(--tabs-border-radius);
  padding: var(--tabs-padding);
  background: var(--or-c-surface-variant);

  &.is-mobile {
    --tab-height: 2.25rem;
    --tab-font-size: var(--fs-1);
    --tab-icon-font-size: 1.5rem;

    /* Inline and compact tabs have the same height */
    &.is-compact {
      --tab-height: 2.375rem;
    }
  }

  &.is-compact {
    --tab-height: 2rem;
    --tabs-padding: 1px;
    flex-direction: column;
  }

  /* Tabs and options have the same dimensions */
  .tab,
  .toggle-button,
  .tab-option {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--tab-height);
    padding: 0 var(--tab-padding);
    font-size: var(--tabs-font-size);
    line-height: 1.5;
    overflow: hidden;
    cursor: pointer;

    .label {
      @include text-overflow;
    }
  }

  .tab {
    flex: 1 1 100%;
  }

  /* Inactive tabs and options have the same colors */
  .tab,
  .tab-option {
    color: var(--or-c-on-surface-variant);
  }

  /* Active tab and toggle button have the same colors */
  .tab.is-active,
  .toggle-button {
    color: var(--or-c-on-background);
    background: var(--or-c-background);
    border-radius: var(--tabs-border-radius);
  }

  .toggle-button,
  .tab-option {
    flex: 0 0 auto;
  }

  .tab-option {
    &:hover,
    &:active {
      font-weight: 700;
    }
  }

  .toggle-button {
    &.is-open {
      .or-icon-root ::v-deep .or-icon-v3 {
        color: inherit;
      }
    }

    .label {
      flex: 1 1 100%;
      margin: 0 1.75rem 0 0.5rem;
      text-align: center;
    }

    .or-icon-root {
      ::v-deep .or-icon-v3 {
        font-size: var(--tab-icon-font-size);
        color: var(--or-c-outline);
      }
    }
  }
}
</style>
